import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPendingCAEvents,
  getPendingCAEvent,
  approvePendingCA,
  rejectPendingCA,
  editAndApprovePendingCA,
  addAnnotation,
  bulkApprovePendingCA,
} from '../../api/pendingCaApi';
import { subscribe, onReconnected, useWebSocket } from '../../hooks/useWebSocket';
import type {
  PendingCAFilters,
  PendingCAListResponse,
  PendingCAPushEvent,
  BulkApproveRequest,
} from './pendingCa.types';

// 권위: docs/ARCH_DASHBOARD.md §4.10
// - WebSocket 단절 시 30초 폴링 폴백.
// - 실시간 신규 pending 도착 시 auditId 를 highlight 큐에 추가 + 토스트 콜백 호출.

const QK_LIST = 'pending-ca-list';
const QK_DETAIL = 'pending-ca-detail';

export type ToastFn = (msg: string, tone?: 'info' | 'success' | 'error' | 'warn') => void;

export interface PendingCARealtimeState {
  highlightedIds: Set<number>;
  lastPush: PendingCAPushEvent | null;
}

/**
 * 목록 + 실시간 + 폴링 폴백 통합 훅.
 * @param onToast 신규 pending 수신 시 토스트 노출용 콜백 (선택)
 */
export function usePendingCAList(filters: PendingCAFilters, onToast?: ToastFn) {
  const { status: wsStatus } = useWebSocket();
  const queryClient = useQueryClient();
  const query = useQuery<PendingCAListResponse>({
    queryKey: [QK_LIST, filters],
    queryFn: () => getPendingCAEvents(filters),
    // WS 단절 시에만 30s 폴링. 연결되어있으면 실시간 푸시만 신뢰.
    refetchInterval: wsStatus === 'connected' ? false : 30_000,
    staleTime: 10_000,
  });

  // 실시간 하이라이트 큐 — 최근 10개 auditId, 각각 10초 뒤 자동 제거.
  const [highlightedIds, setHighlightedIds] = useState<Set<number>>(new Set());
  const [lastPush, setLastPush] = useState<PendingCAPushEvent | null>(null);
  const timerRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    const off = subscribe('/topic/pending-ca', (msg) => {
      if (!msg.body) return;
      let parsed: PendingCAPushEvent;
      try {
        parsed = JSON.parse(msg.body) as PendingCAPushEvent;
      } catch {
        return;
      }
      setLastPush(parsed);
      const aid = parsed.auditId;
      if (typeof aid === 'number') {
        setHighlightedIds((prev) => {
          const next = new Set(prev);
          next.add(aid);
          return next;
        });
        const existing = timerRef.current.get(aid);
        if (existing) window.clearTimeout(existing);
        const t = window.setTimeout(() => {
          setHighlightedIds((prev) => {
            const next = new Set(prev);
            next.delete(aid);
            return next;
          });
          timerRef.current.delete(aid);
        }, 10_000);
        timerRef.current.set(aid, t);
      }
      if (onToast) {
        const symbol = parsed.symbol ?? 'UNKNOWN';
        const eventType = parsed.eventType ?? 'ca-event';
        const conf = parsed.confidence != null ? ` (conf ${parsed.confidence.toFixed(2)})` : '';
        onToast(`신규 Pending: ${symbol} ${eventType}${conf}`, 'warn');
      }
      // 상단 요약/목록 refresh
      queryClient.invalidateQueries({ queryKey: [QK_LIST] });
    });
    return () => {
      off();
      timerRef.current.forEach((t) => window.clearTimeout(t));
      timerRef.current.clear();
    };
  }, [onToast, queryClient]);

  // 재연결 시 재조회
  useEffect(() => {
    const off = onReconnected(() => {
      queryClient.invalidateQueries({ queryKey: [QK_LIST] });
    });
    return off;
  }, [queryClient]);

  return { ...query, highlightedIds, lastPush };
}

export function usePendingCADetail(auditId: number | null) {
  return useQuery({
    queryKey: [QK_DETAIL, auditId],
    queryFn: () => getPendingCAEvent(auditId as number),
    enabled: auditId != null,
    staleTime: 5_000,
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [QK_LIST] });
  queryClient.invalidateQueries({ queryKey: [QK_DETAIL] });
}

export function useApproveMutation(onToast?: ToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (auditId: number) => approvePendingCA(auditId),
    onSuccess: (_, auditId) => {
      invalidateAll(qc);
      onToast?.(`승인 완료 (audit_id=${auditId})`, 'success');
    },
    onError: (err: Error) => onToast?.(`승인 실패: ${err.message}`, 'error'),
  });
}

export function useRejectMutation(onToast?: ToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, note }: { auditId: number; note: string }) =>
      rejectPendingCA(auditId, note),
    onSuccess: (_, vars) => {
      invalidateAll(qc);
      onToast?.(`반려 완료 (audit_id=${vars.auditId})`, 'success');
    },
    onError: (err: Error) => onToast?.(`반려 실패: ${err.message}`, 'error'),
  });
}

export function useEditApproveMutation(onToast?: ToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      auditId,
      extractedEvent,
      note,
    }: {
      auditId: number;
      extractedEvent: Record<string, unknown>;
      note?: string;
    }) => editAndApprovePendingCA(auditId, extractedEvent, note),
    onSuccess: (_, vars) => {
      invalidateAll(qc);
      onToast?.(`수정 후 승인 완료 (audit_id=${vars.auditId})`, 'success');
    },
    onError: (err: Error) => onToast?.(`수정 승인 실패: ${err.message}`, 'error'),
  });
}

export function useAnnotationMutation(onToast?: ToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, note }: { auditId: number; note: string }) =>
      addAnnotation(auditId, note),
    onSuccess: () => {
      invalidateAll(qc);
      onToast?.('주석 추가됨', 'info');
    },
    onError: (err: Error) => onToast?.(`주석 실패: ${err.message}`, 'error'),
  });
}

export function useBulkApproveMutation(onToast?: ToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: BulkApproveRequest) => bulkApprovePendingCA(req),
    onSuccess: (res) => {
      invalidateAll(qc);
      onToast?.(
        `일괄 승인: 성공 ${res.succeeded}건 / 실패 ${res.failed}건 (요청 ${res.requested}건)`,
        res.failed > 0 ? 'warn' : 'success'
      );
    },
    onError: (err: Error) => onToast?.(`일괄 승인 실패: ${err.message}`, 'error'),
  });
}
